import type { DateValue } from "@internationalized/date";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { ProductMetrics, ProductSort, ProductStatus } from "@unbox/api";
import { PRODUCTS_NEGATABLE_FILTERS } from "@unbox/api";
import { handleError } from "@/api/error";
import { toNotQuery } from "@/providers/filter-registry";
import { type Pagination, toQuery } from "./pagination";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

type StockStatus = "in_stock" | "low_stock" | "out_of_stock";

type ProductMetricsFilters = {
  text: string;
  maxGmv?: number;
  minGmv?: number;
  status?: ProductStatus[];
  creatorIds?: string[];
  creatorListIds?: string[];
  creatorClassifications?: string[];
  stockStatus?: StockStatus[];
  minUnitsSold?: number;
  maxUnitsSold?: number;
  minVideos?: number;
  maxVideos?: number;
  minAov?: number;
  maxAov?: number;
  minPrice?: number;
  maxPrice?: number;
  minStockLevel?: number;
  maxStockLevel?: number;
  categoryIds?: string[];
  not?: string[];
};
export const useProductsMetrics = ({
  range,
  pagination,
  text,
  sort,
  maxGmv,
  minGmv,
  status,
  creatorIds,
  creatorListIds,
  creatorClassifications,
  stockStatus,
  minUnitsSold,
  maxUnitsSold,
  minVideos,
  maxVideos,
  minAov,
  maxAov,
  minPrice,
  maxPrice,
  minStockLevel,
  maxStockLevel,
  categoryIds,
  not,
  ...rest
}: {
  range: { start?: DateValue; end?: DateValue } | null;
  pagination: Pagination;
  sort: { column: ProductSort; direction: "asc" | "desc" };
} & ProductMetricsFilters) => {
  rest satisfies Record<string, never>;
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: [
      "product-overview",
      scope.merchantId,
      scope.workspaceId,
      range?.start?.toString(),
      range?.end?.toString(),
      pagination.limit,
      pagination.offset,
      text,
      sort.column,
      sort.direction,
      maxGmv,
      minGmv,
      status,
      creatorIds?.toString(),
      creatorListIds?.toString(),
      creatorClassifications?.toString(),
      stockStatus?.toString(),
      minUnitsSold,
      maxUnitsSold,
      minVideos,
      maxVideos,
      minAov,
      maxAov,
      minPrice,
      maxPrice,
      minStockLevel,
      maxStockLevel,
      categoryIds?.toString(),
      not?.toString(),
    ],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].explorer.products.$get({
            param: scope.param,
            query: {
              start: range!.start!.toString(),
              end: range!.end!.toString(),
              ...toQuery(pagination),
              text: text === "" ? undefined : text,
              column: sort.column,
              direction: sort.direction,
              maxGmv: maxGmv?.toString(),
              minGmv: minGmv?.toString(),
              status,
              creatorIds,
              creatorListIds,
              creatorClassifications,
              stockStatus,
              minUnitsSold: minUnitsSold?.toString(),
              maxUnitsSold: maxUnitsSold?.toString(),
              minVideos: minVideos?.toString(),
              maxVideos: maxVideos?.toString(),
              minAov: minAov?.toString(),
              maxAov: maxAov?.toString(),
              minPrice: minPrice?.toString(),
              maxPrice: maxPrice?.toString(),
              minStockLevel: minStockLevel?.toString(),
              maxStockLevel: maxStockLevel?.toString(),
              categoryIds,
              not: toNotQuery(not, PRODUCTS_NEGATABLE_FILTERS),
            },
          })
        : await client.merchant[":merchantId"].explorer.products.$get({
            param: scope.param,
            query: {
              start: range!.start!.toString(),
              end: range!.end!.toString(),
              ...toQuery(pagination),
              text: text === "" ? undefined : text,
              column: sort.column,
              direction: sort.direction,
              maxGmv: maxGmv?.toString(),
              minGmv: minGmv?.toString(),
              status,
              creatorIds,
              creatorListIds,
              creatorClassifications,
              stockStatus,
              minUnitsSold: minUnitsSold?.toString(),
              maxUnitsSold: maxUnitsSold?.toString(),
              minVideos: minVideos?.toString(),
              maxVideos: maxVideos?.toString(),
              minAov: minAov?.toString(),
              maxAov: maxAov?.toString(),
              minPrice: minPrice?.toString(),
              maxPrice: maxPrice?.toString(),
              minStockLevel: minStockLevel?.toString(),
              maxStockLevel: maxStockLevel?.toString(),
              categoryIds,
              not: toNotQuery(not, PRODUCTS_NEGATABLE_FILTERS),
            },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!range?.start && !!range?.end,
  });
};

export const useProductsCount = ({
  range,
  text,
  maxGmv,
  minGmv,
  status,
  creatorIds,
  creatorListIds,
  creatorClassifications,
  stockStatus,
  minUnitsSold,
  maxUnitsSold,
  minVideos,
  maxVideos,
  minAov,
  maxAov,
  minPrice,
  maxPrice,
  minStockLevel,
  maxStockLevel,
  categoryIds,
  not,
  ...rest
}: {
  range: { start?: DateValue; end?: DateValue } | null;
} & ProductMetricsFilters) => {
  rest satisfies Record<string, never>;
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: [
      "products-count",
      scope.merchantId,
      scope.workspaceId,
      range?.start?.toString(),
      range?.end?.toString(),
      text,
      maxGmv,
      minGmv,
      status,
      creatorIds?.toString(),
      creatorListIds?.toString(),
      creatorClassifications?.toString(),
      stockStatus?.toString(),
      minUnitsSold,
      maxUnitsSold,
      minVideos,
      maxVideos,
      minAov,
      maxAov,
      minPrice,
      maxPrice,
      minStockLevel,
      maxStockLevel,
      categoryIds?.toString(),
      not?.toString(),
    ],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].explorer.products.count.$get({
            param: scope.param,
            query: {
              start: range!.start!.toString(),
              end: range!.end!.toString(),
              text: text === "" ? undefined : text,
              maxGmv: maxGmv?.toString(),
              minGmv: minGmv?.toString(),
              status,
              creatorIds,
              creatorListIds,
              creatorClassifications,
              stockStatus,
              minUnitsSold: minUnitsSold?.toString(),
              maxUnitsSold: maxUnitsSold?.toString(),
              minVideos: minVideos?.toString(),
              maxVideos: maxVideos?.toString(),
              minAov: minAov?.toString(),
              maxAov: maxAov?.toString(),
              minPrice: minPrice?.toString(),
              maxPrice: maxPrice?.toString(),
              minStockLevel: minStockLevel?.toString(),
              maxStockLevel: maxStockLevel?.toString(),
              categoryIds,
              not: toNotQuery(not, PRODUCTS_NEGATABLE_FILTERS),
            },
          })
        : await client.merchant[":merchantId"].explorer.products.count.$get({
            param: scope.param,
            query: {
              start: range!.start!.toString(),
              end: range!.end!.toString(),
              text: text === "" ? undefined : text,
              maxGmv: maxGmv?.toString(),
              minGmv: minGmv?.toString(),
              status,
              creatorIds,
              creatorListIds,
              creatorClassifications,
              stockStatus,
              minUnitsSold: minUnitsSold?.toString(),
              maxUnitsSold: maxUnitsSold?.toString(),
              minVideos: minVideos?.toString(),
              maxVideos: maxVideos?.toString(),
              minAov: minAov?.toString(),
              maxAov: maxAov?.toString(),
              minPrice: minPrice?.toString(),
              maxPrice: maxPrice?.toString(),
              minStockLevel: minStockLevel?.toString(),
              maxStockLevel: maxStockLevel?.toString(),
              categoryIds,
              not: toNotQuery(not, PRODUCTS_NEGATABLE_FILTERS),
            },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!range?.start && !!range?.end,
  });
};

const CHUNK_SIZE = 200;
export const useProductMetricsDownload = () => {
  const scope = useScopedMerchantClient();

  return useMutation<
    ProductMetrics[],
    void,
    {
      total: number;
      range: { start: DateValue; end: DateValue } | null;
      sort: { column: ProductSort; direction: "asc" | "desc" };
    } & ProductMetricsFilters
  >({
    mutationFn: async ({
      total,
      sort,
      text,
      range,
      maxGmv,
      minGmv,
      status,
      creatorIds,
      creatorListIds,
      creatorClassifications,
      stockStatus,
      minUnitsSold,
      maxUnitsSold,
      minVideos,
      maxVideos,
      minAov,
      maxAov,
      minPrice,
      maxPrice,
      minStockLevel,
      maxStockLevel,
      categoryIds,
      not,
      ...rest
    }) => {
      rest satisfies Record<string, never>;
      const chunks = [...Array(Math.ceil(total / CHUNK_SIZE)).keys()];

      const requests = chunks.map(async (page) => {
        const res = scope.workspaceId
          ? await client.workspace[":workspaceId"].merchant[":merchantId"].explorer.products.$get({
              param: scope.param,
              query: {
                start: range!.start!.toString(),
                end: range!.end!.toString(),
                limit: CHUNK_SIZE.toString(),
                offset: (CHUNK_SIZE * page).toString(),
                text: text === "" ? undefined : text,
                column: sort.column,
                direction: sort.direction,
                maxGmv: maxGmv?.toString(),
                minGmv: minGmv?.toString(),
                status,
                creatorIds,
                creatorListIds,
                creatorClassifications,
                stockStatus,
                minUnitsSold: minUnitsSold?.toString(),
                maxUnitsSold: maxUnitsSold?.toString(),
                minVideos: minVideos?.toString(),
                maxVideos: maxVideos?.toString(),
                minAov: minAov?.toString(),
                maxAov: maxAov?.toString(),
                minPrice: minPrice?.toString(),
                maxPrice: maxPrice?.toString(),
                minStockLevel: minStockLevel?.toString(),
                maxStockLevel: maxStockLevel?.toString(),
                categoryIds,
                not: toNotQuery(not, PRODUCTS_NEGATABLE_FILTERS),
              },
            })
          : await client.merchant[":merchantId"].explorer.products.$get({
              param: scope.param,
              query: {
                start: range!.start!.toString(),
                end: range!.end!.toString(),
                limit: CHUNK_SIZE.toString(),
                offset: (CHUNK_SIZE * page).toString(),
                text: text === "" ? undefined : text,
                column: sort.column,
                direction: sort.direction,
                maxGmv: maxGmv?.toString(),
                minGmv: minGmv?.toString(),
                status,
                creatorIds,
                creatorListIds,
                creatorClassifications,
                stockStatus,
                minUnitsSold: minUnitsSold?.toString(),
                maxUnitsSold: maxUnitsSold?.toString(),
                minVideos: minVideos?.toString(),
                maxVideos: maxVideos?.toString(),
                minAov: minAov?.toString(),
                maxAov: maxAov?.toString(),
                minPrice: minPrice?.toString(),
                maxPrice: maxPrice?.toString(),
                minStockLevel: minStockLevel?.toString(),
                maxStockLevel: maxStockLevel?.toString(),
                categoryIds,
                not: toNotQuery(not, PRODUCTS_NEGATABLE_FILTERS),
              },
            });
        return res.json();
      });

      try {
        const response = await Promise.all(requests);
        return response.flatMap((res) => res.products);
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
  });
};
